import store from '../../redux/store/store';
import {
  addGiftCard,
  addPaymentMethod,
  updatePaymentCard,
  updateIsSplitPayment,
  updateAmountOnSplit,
  updateGiftCard,
} from '../../redux/slices/checkoutSlice';
import { queryClient } from '../../react-query-hooks/queryClient';
import { CURRENT_USER } from '../../constants';
import { IGiftCard } from '../../models/giftCard.model';
import { splitAmount } from './PaymentHelper';

interface SavedCard {
  value: string;
  label: string;
  id: string | number;
  is_default: boolean;
  number: string;
  valid: string | number;
}

declare global {
  interface Window {
    CafeZupasCheckout?: {
      selectSavedCard: (cardId: string | number) => { ok: boolean; reason?: string };
      /** Same cache key as `useGetGiftCards`: `["giftCards", customer_id]`. */
      selectSavedGiftCard: (giftCardId: string | number) => { ok: boolean; reason?: string };
    };
  }
}

function getCurrentUserId(): string | null {
  try {
    const raw = localStorage.getItem(CURRENT_USER);
    return raw ? JSON.parse(raw)?.id ?? null : null;
  } catch {
    return null;
  }
}

export function registerOptimizelyBridge(): void {
  window.CafeZupasCheckout = {
    selectSavedCard(cardId: string | number): { ok: boolean; reason?: string } {
      const userId = getCurrentUserId();
      const cards = queryClient.getQueryData<SavedCard[]>(['paymentMethod', userId]);
      const selectedCard: any = cards?.find((c) => String(c.id) === String(cardId));

      if (!selectedCard) {
        return { ok: false, reason: 'card_not_found' };
      }

      const state = store.getState().checkout;

      if (state.isSplitPayment) {
        return { ok: false, reason: 'split_payment_not_supported' };
      }

      const totalPrice = state.billing?.total_price;
      if (totalPrice == null || Number.isNaN(Number(totalPrice))) {
        return { ok: false, reason: 'checkout_not_ready' };
      }

      store.dispatch(updateGiftCard([]));
      store.dispatch(updatePaymentCard([]));

      // Mirror the exact transformation done in PaymentModal handleFormSubmission (line 214)
      const paymentMethod = {
        id: selectedCard.id,
        name: selectedCard.name,
        number: `Card Ending in *${selectedCard.card.number}`,
        valid: `Valid Until ${selectedCard.card.exp_year}`,
        amount: totalPrice,
      };
      store.dispatch(addPaymentMethod(paymentMethod));
      store.dispatch(updateIsSplitPayment(false));
      store.dispatch(updateAmountOnSplit({ totalAmount: totalPrice }));

      return { ok: true };
    },

    selectSavedGiftCard(giftCardId: string | number): { ok: boolean; reason?: string } {
      const userId = getCurrentUserId();
      const records = queryClient.getQueryData<IGiftCard[]>(['giftCards', userId]);
      const selectedGift = records?.find((g) => String(g.id) === String(giftCardId));

      if (!userId || !selectedGift?.gift_card_no) {
        return { ok: false, reason: 'gift_card_not_found' };
      }

      const checkoutState = store.getState().checkout;

      if (checkoutState.isSplitPayment) {
        return { ok: false, reason: 'split_payment_not_supported' };
      }

      const totalPrice = checkoutState.billing?.total_price;
      if (totalPrice == null || Number.isNaN(Number(totalPrice))) {
        return { ok: false, reason: 'checkout_not_ready' };
      }

      // Mirror CheckoutGiftCards.saveSelectedPaymentMethod + handleFormSubmission (non-split path)
      const amount = splitAmount(checkoutState, Number(totalPrice));
      const giftCardPayload = {
        id: selectedGift.id,
        name: selectedGift.name ?? '',
        gift_card_no: selectedGift.gift_card_no,
        amount,
      };

      store.dispatch(updatePaymentCard([]));
      if (checkoutState.giftCard.length > 0) {
        store.dispatch(updateGiftCard([]));
      }
      store.dispatch(addGiftCard(giftCardPayload));
      store.dispatch(updateIsSplitPayment(false));
      store.dispatch(updateAmountOnSplit({ totalAmount: Number(totalPrice) }));

      return { ok: true };
    },
  };
}
